import { ImageUp, Trash2 } from 'lucide-react';
import { useRef, type ReactNode } from 'react';
import { VENTURE_LOGO_MAX_BYTES } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { VentureLogo } from '../../../components/VentureLogo';
import { errorMessage } from '../../../lib/api-client';
import { useCanEdit, useRequiredSession, type Venture } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { useChangeVentureLogo } from '../api/settings-api';
import styles from './VentureLogosCard.module.css';

const BYTES_PER_KB = 1024;

/**
 * ADMIN-15's venture logos (DECISIONS-LOG 2026-10-06). Optional: a venture without one keeps
 * the building icon in the switcher. A file goes up the moment it is chosen — there is no
 * draft to save, so this card sits outside the settings form and its Save button.
 */
export function VentureLogosCard(): ReactNode {
  const ventures = useRequiredSession().ventures;
  if (ventures.length === 0) return null;
  return (
    <Card title="Venture logos" meta="Optional · PNG or JPG, up to 200 KB · shown in the venture switcher">
      <ul className={styles.list}>
        {ventures.map((venture) => (
          <VentureLogoRow key={venture.id} venture={venture} />
        ))}
      </ul>
    </Card>
  );
}

function VentureLogoRow({ venture }: { readonly venture: Venture }): ReactNode {
  const canEdit = useCanEdit('admin_config');
  const change = useChangeVentureLogo();
  const show = useToasts((state) => state.show);
  const input = useRef<HTMLInputElement | null>(null);
  const hasLogo = venture.logoVersion !== null;

  async function upload(file: File): Promise<void> {
    if (file.size > VENTURE_LOGO_MAX_BYTES) {
      show({ tone: 'danger', message: 'File is too large. Maximum 200 KB.', detail: `That file is ${Math.ceil(file.size / BYTES_PER_KB)} KB.` });
      return;
    }
    try {
      await change.mutateAsync({ kind: 'upload', ventureId: venture.id, file });
      show({ tone: 'success', message: `${venture.name} logo updated` });
    } catch (cause) {
      show({ tone: 'danger', message: "Couldn't upload the logo", detail: errorMessage(cause) });
    }
  }

  async function remove(): Promise<void> {
    try {
      await change.mutateAsync({ kind: 'remove', ventureId: venture.id });
      show({ tone: 'success', message: `${venture.name} logo removed`, detail: 'The building icon shows in its place.' });
    } catch (cause) {
      show({ tone: 'danger', message: "Couldn't remove the logo", detail: errorMessage(cause) });
    }
  }

  return (
    <li className={styles.row}>
      <VentureLogo venture={venture} size="lg" />
      <span className={styles.name}>
        <span className={styles.title}>{venture.name}</span>
        <span className={styles.meta}>{hasLogo ? venture.code : `${venture.code} · no logo — the building icon shows`}</span>
      </span>
      {canEdit && (
        <span className={styles.actions}>
          <input
            ref={input}
            className={styles.hiddenFile}
            type="file"
            accept="image/png,image/jpeg"
            aria-label={`Choose a logo file for ${venture.name}`}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) void upload(file);
            }}
          />
          <Button variant="outline" icon={ImageUp} isPending={change.isPending && change.variables?.kind === 'upload'} onClick={() => input.current?.click()}>
            {hasLogo ? 'Replace' : 'Upload a logo'}
          </Button>
          {hasLogo && (
            <Button variant="ghost" icon={Trash2} isPending={change.isPending && change.variables?.kind === 'remove'} onClick={() => void remove()}>
              Remove
            </Button>
          )}
        </span>
      )}
    </li>
  );
}
